// ============================================================
// attack/context - one render's worth of attack inputs.
//
// The hook's parameters with their defaults applied, plus the two reads of
// the actor's condition context that every stage of the pipeline makes.
// Built once per render by `useAttackExecution` and handed to each stage,
// so no stage re-derives (or re-defaults) what another already has.
// ============================================================
import type { ExposureBearer, ExposureReader } from '../../engine/exposures';
import { DEFAULT_UNNATURAL_DOS_MODE } from '../../engine/unnaturalDos';
import type { UseAttackExecutionParams } from './types';

/** The parameters that carry a default when the caller leaves them out. */
type Defaulted =
  | 'unnaturalDosMode'
  | 'protagonistRules'
  | 'averagedDamage'
  | 'calledShotExposure'
  | 'calledShotIgnored'
  | 'isAimHalf'
  | 'isAimFull'
  | 'blastScatter'
  | 'reliableNeverJams'
  | 'attackCustomMods'
  | 'suppressing'
  | 'attackBlockedReason'
  | 'indirectNoLoS';

export type AttackContext = Omit<UseAttackExecutionParams, Defaulted> &
  Required<Pick<UseAttackExecutionParams, Defaulted>> & {
    /** THE AURAS THE ACTOR STANDS UNDER (v8.368.8): one list, read by every seam. */
    actorAuras: string[];
    /** The actor's team tag, or null - what a named `@team` exposure scope is read by. */
    attackerTeamTag: string | null;
    /** The actor's side (2026-10-23), or null - what `@ally` / `@hostile` and an effect's `affects` compare. */
    attackerSideTag: string | null;
    /** A vehicle's ram (attack/ram): the beat column says `ram` (plan design-gaps, K2). */
    ramming?: boolean;
  };

/**
 * Apply the defaults and derive the actor reads.
 *
 * Destructuring defaults, not `??`: a default applies only to an ABSENT
 * value, exactly as the hook's own parameter list always did. Declarative:
 * one default per line, which is what makes it longer than a function.
 */
export function attackContext({
  unnaturalDosMode = DEFAULT_UNNATURAL_DOS_MODE,
  protagonistRules = false,
  averagedDamage = 'off',
  calledShotExposure = 0,
  calledShotIgnored = false,
  isAimHalf = false,
  isAimFull = false,
  blastScatter = true,
  reliableNeverJams = false,
  attackCustomMods = [],
  suppressing = false,
  attackBlockedReason = null,
  indirectNoLoS = false,
  ...rest
}: UseAttackExecutionParams): AttackContext {
  const actor = rest.actorConditionContext;
  return {
    ...rest,
    unnaturalDosMode,
    protagonistRules,
    averagedDamage,
    calledShotExposure,
    calledShotIgnored,
    isAimHalf,
    isAimFull,
    blastScatter,
    reliableNeverJams,
    attackCustomMods,
    suppressing,
    attackBlockedReason,
    indirectNoLoS,
    actorAuras: (actor?.auras as string[] | undefined) ?? [],
    attackerTeamTag: (actor?.teamTag as string | null | undefined) ?? null,
    attackerSideTag: (actor?.sideTag as string | null | undefined) ?? null,
  };
}

/** The aimed target as an exposure BEARER - what the to-hit roll, hit count and jam read. */
export function aimedBearer(ctx: AttackContext): ExposureBearer {
  const target = ctx.currentTab?.target;
  return {
    conditions: target?.conditions,
    exposures: target?.exposures,
    teamTag: target?.teamTag,
    sideTag: target?.sideTag,
  };
}

/** The attacker as an exposure READER: their team, their auras, the kind of attack. */
export function actorReader(ctx: AttackContext, isMelee: boolean): ExposureReader {
  return { teamTag: ctx.attackerTeamTag, sideTag: ctx.attackerSideTag, auras: ctx.actorAuras, isMelee };
}

/**
 * THE CONTEXT A PROMPT IS ANSWERED IN (2026-10-22, plan muster).
 *
 * A re-roll or Furious Assault prompt can wait while the viewer switches who
 * they act as - one click on the muster. The answer is the SWINGER's, so it
 * resumes in the context of the last render that acted as them (`held`, by
 * the prompt's `actorId`), not the live one: their modifiers, stats,
 * conditions and callbacks, and the tabs as projected from THEIR position
 * (cover is a relation to the attacker, so the live tabs are the wrong
 * person's view), with the hit pipeline that reads them. Only the prompts
 * and their setters come from the live render - they are what is being
 * answered and cleared.
 *
 * The held render is as fresh as the moment you switched away: no new attack
 * is made while either prompt is open (attack/swing), but at a shared table
 * another screen can still change the target in between, and the answer
 * reads it as it stood at the switch - the price of answering from the
 * swinger's own view (recorded in the plan's report). With no held render
 * for that actor (this screen never held them while the prompt waited), the
 * live context answers, as it always did.
 */
export function answeringContext(live: AttackContext, held: AttackContext | undefined): AttackContext {
  if (!held || held === live) return live;
  const { pendingReroll, setPendingReroll, pendingBonusAttack, setPendingBonusAttack } = live;
  return { ...held, pendingReroll, setPendingReroll, pendingBonusAttack, setPendingBonusAttack };
}
